import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Carousel, CarouselCaption, CarouselControl, CarouselIndicators, CarouselItem } from 'reactstrap';
import { autoinject } from '../Utils/AutoInject';
import { bind } from '../Utils/Bind';

export interface IItemCarouselProps<T extends any> {
    readonly items: T[];
    renderItem(item: T): React.ReactNode;
    readonly className?: string;
    readonly dark?: boolean;
}

@autoinject
@observer
export class ItemCarousel<T extends any> extends React.Component<IItemCarouselProps<T>> {
    public render() {
        const { items } = this.props;
        return (
            <Carousel
                dark={this.props.dark}
                className={this.props.dark ? this.className : this.className + ' carousel-light'}
                interval={false}
                slide
                activeIndex={this.activeIndex}
                next={this.next}
                previous={this.previous}
            >
                {items.length > 1 && (
                    <CarouselIndicators
                        items={items as any}
                        activeIndex={this.activeIndex}
                        onClickHandler={this.goToIndex}
                    />
                )}
                {items.map((i, idx) => (
                    <CarouselItem
                        onExiting={() => this.setAnimating(true)}
                        onExited={() => this.setAnimating(false)}
                        key={idx}
                    >
                        {this.props.renderItem(i)}
                    </CarouselItem>
                ))}
                {items.length > 1 && (
                    <>
                        <CarouselControl direction="prev" directionText="Previous" onClickHandler={this.previous} />
                        <CarouselControl direction="next" directionText="Next" onClickHandler={this.next} />
                    </>
                )}
            </Carousel>
        );
    }

    @bind
    @action
    private next() {
        if (this.animating) return;
        const nextIndex = this.activeIndex === this.props.items.length - 1 ? 0 : this.activeIndex + 1;
        this.setActiveIndex(nextIndex);
    }

    @bind
    @action
    private previous() {
        if (this.animating) return;
        const nextIndex = this.activeIndex === 0 ? this.props.items.length - 1 : this.activeIndex - 1;
        this.setActiveIndex(nextIndex);
    }

    @bind
    @action
    private setActiveIndex(index: number) {
        this.activeIndex = index;
    }

    @bind
    @action
    private setAnimating(animating: boolean) {
        this.animating = animating;
    }

    @bind
    @action
    private goToIndex(index: number) {
        this.activeIndex = index;
    }

    @computed
    private get className() {
        let className = this.props.className;
        if (this.props.items.length > 1) {
            className = className + ' multiple';
        }

        return className;
    }

    @observable
    private activeIndex = 0;
    @observable
    private animating = false;
}
